"use client";
import * as React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import { inputsCustomizations } from "./customizations/inputs";
import { dataDisplayCustomizations } from "./customizations/dataDisplay.tsx";
import { feedbackCustomizations } from "./customizations/feedback";
import { navigationCustomizations } from "./customizations/navigation";
import { surfacesCustomizations } from "./customizations/surfaces";
import { colorSchemes, typography, shadows, shape } from "./themePrimitives";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v15-appRouter";
import { CssBaseline } from "@mui/material";
// Note: You may not need this ClassNameGenerator unless you have a specific reason for it.
// It can also be a source of hydration issues if not handled carefully.
import { unstable_ClassNameGenerator } from "@mui/material/className";

interface AppThemeProps {
  children: React.ReactNode;
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions["components"];
  nonce: string;
}

// Consider removing this unless you are intentionally avoiding 'Mui' prefixes.
unstable_ClassNameGenerator.configure((componentName) =>
  componentName.replace("Mui", "sobecblobec-")
);

export default function AppTheme(props: AppThemeProps) {
  const { children, disableCustomTheme, themeComponents, nonce } = props;
  // Create the theme as a local constant within the component
  const theme = React.useMemo(() => {
    return disableCustomTheme
      ? createTheme({}) // Still create a default theme if disabled
      : createTheme({
          cssVariables: {
            colorSchemeSelector: "data-mui-color-scheme",
            cssVarPrefix: "template",
          },
          colorSchemes,
          typography,
          shadows,
          shape,
          components: {
            ...inputsCustomizations,
            ...dataDisplayCustomizations,
            ...feedbackCustomizations,
            ...navigationCustomizations,
            ...surfacesCustomizations,
            ...themeComponents,
            MuiDivider: {
              styleOverrides: {
                root: ({ theme }) => ({
                  borderBottomWidth: 2,
                  marginBottom: theme.spacing(2),
                }),
              },
            },
          },
        });
  }, [disableCustomTheme, themeComponents]);

  if (disableCustomTheme) {
    // It's better to provide a default theme than a fragment
    // to ensure consumers of the theme context don't crash.
    return (
      <AppRouterCacheProvider options={{ enableCssLayer: true, nonce: nonce }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </AppRouterCacheProvider>
    );
  }

  return (
    <AppRouterCacheProvider options={{ enableCssLayer: true, nonce: nonce }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppRouterCacheProvider>
  );
}
